/**
 * Ground-plane projection of a drone camera's field of view.
 *
 * Replaces the fixed 65-degree / 75 m wedge COP used to draw for any streaming
 * device with the actual quadrilateral the camera sees, derived from altitude,
 * gimbal attitude and lens field of view.
 *
 * Model and its limits:
 *
 * - Pinhole camera. The four corner rays of the frustum are intersected with a
 *   flat ground plane at the aircraft's takeoff elevation.
 * - `altitudeAglM` is height above the takeoff point, which is what DJI MSDK v4
 *   reports. It is not MSL and not height above the terrain under the aircraft,
 *   so over sloping ground the footprint is wrong in proportion to the relief.
 *   A DEM lookup is the fix if that ever matters.
 * - No lens distortion correction. Real footprints have slightly bowed edges.
 *
 * When the camera is near or above horizontal some corner rays never meet the
 * ground, and others meet it so far out that the answer is meaningless. Both are
 * cut off at the altitude-scaled limit described on [MIN_DEPRESSION_DEG], and the
 * result is flagged `horizonClipped` so the caller can render it as an open-ended
 * swathe rather than implying a hard far edge that was actually invented.
 */

export interface CameraFootprintInput {
  latitude: number;
  longitude: number;
  /** Metres above the ground plane (the takeoff point). */
  altitudeAglM: number;
  /** Degrees. 0 is horizontal, negative looks down. Nadir is -90. */
  gimbalPitchDeg: number;
  /** Degrees clockwise from north, the direction the camera faces. */
  cameraYawDeg: number;
  /** Degrees of camera roll about the optical axis. Usually ~0. */
  gimbalRollDeg?: number;
  /**
   * Whether the aircraft is airborne. Only `false` suppresses the footprint;
   * absent means "unknown", so callers without the flag keep working.
   */
  isFlying?: boolean | null;
  /** Horizontal field of view in degrees. */
  hfovDeg: number;
  /** Vertical field of view in degrees. */
  vfovDeg: number;
  /**
   * Absolute ceiling on ray length. The binding limit is normally the
   * altitude-scaled one from [MIN_DEPRESSION_DEG]; this only caps the extreme
   * case where even that allows an implausible distance.
   */
  maxRangeM?: number;
}

export interface CameraFootprint {
  /** Corner ring, closed (first point repeated), as [lon, lat]. */
  ring: Array<[number, number]>;
  /** True when at least one corner ray was clamped instead of hitting ground. */
  horizonClipped: boolean;
  /** Ground distance from nadir to the footprint centre, metres. */
  centerDistanceM: number;
  /** Approximate ground area in square metres. */
  areaM2: number;
}

const DEG = Math.PI / 180;
const EARTH_RADIUS_M = 6378137;

/**
 * Hard ceiling on how far a corner ray is followed, whatever the geometry says.
 * Beyond this the flat-ground assumption is meaningless anyway. Only reached at
 * altitudes far above normal operation; [MIN_DEPRESSION_DEG] binds first.
 */
export const DEFAULT_MAX_RANGE_M = 2000;

/**
 * Shallowest look-down angle still treated as observation.
 *
 * The geometry alone does not bound the footprint usefully. A camera a few
 * degrees below horizontal does intersect the ground plane, but kilometres out,
 * and the old fixed 5 km clamp turned that into a genuine measurement: unioned
 * along a flight path it produced coverage tens of square kilometres wide from a
 * short sortie, which is what made "area observed" unusable.
 *
 * Two things go wrong at a shallow angle, and both argue for the same cut-off:
 *
 * - Precision. The ground intercept goes as altitude / tan(depression), so at 5
 *   degrees a single metre of altitude error moves the far edge by 11 m, and
 *   `altitudeAglM` is height above the takeoff point rather than above the ground
 *   actually being looked at. The number stops meaning anything before the
 *   geometry stops producing one.
 * - Resolution. Ground sample distance grows with slant range, so past a few
 *   hundred metres a 720p frame is metres per pixel — a direction the camera was
 *   pointed, not ground anyone observed.
 *
 * 10 degrees is a judgement, not a derivation. At 100 m it allows about 570 m of
 * range, where a 720p frame is around 0.7 m per pixel — coarse, but still ground
 * someone could reasonably claim to have observed. It was chosen over a stricter
 * 15 because the corners of a wide oblique frame are shallower than its centre
 * line: a camera pitched 45 degrees down with a 60 degree field of view has
 * corner rays at 13.4 degrees, and cutting those would trim a legitimate oblique
 * view. Raise it to be stricter about what counts as observed, at the cost of
 * clipping the corners of steep wide-angle views.
 */
export const MIN_DEPRESSION_DEG = 10;

/**
 * How far the footprint may extend for a given altitude.
 *
 * Scales with height, which is the behaviour to want: climbing genuinely does let
 * the camera see further, while a low pass cannot claim the horizon.
 */
export function maxUsefulRangeM(
  altitudeAglM: number,
  absoluteCapM: number = DEFAULT_MAX_RANGE_M
): number {
  const geometric = altitudeAglM / Math.tan(MIN_DEPRESSION_DEG * DEG);
  return Math.min(absoluteCapM, geometric);
}

/**
 * Below this the aircraft is on or barely off the ground and no footprint is
 * projected at all.
 *
 * A bare "altitude > 0" test is not enough. On 11 Aug a Mavic Pro sitting on the
 * ground reported altitudes up to 0.9 m with the camera near level, and the
 * projector dutifully returned 2,836 acres: at that height every corner ray is
 * nearly horizontal, so all four clamp at [DEFAULT_MAX_RANGE_M] and the quad
 * becomes an enormous fiction. Sub-metre readings are also within GPS noise.
 */
export const MIN_AIRBORNE_ALT_M = 2;

/**
 * Minimum downward component before a ray counts as hitting the ground.
 * A ray a hair below horizontal technically intersects, but kilometres away
 * and at an angle where a metre of altitude error moves the point enormously.
 */
const MIN_DOWNWARD = 1e-3;

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * True when there is enough telemetry to project a footprint at all.
 * A grounded or unlocated aircraft has no meaningful footprint.
 *
 * `isFlying === false` is decisive on its own: an aircraft that reports itself
 * on the ground has no footprint no matter what altitude it claims.
 */
export function canProjectFootprint(
  input: Partial<CameraFootprintInput> | null | undefined
): input is CameraFootprintInput {
  if (!input) return false;
  if (input.isFlying === false) return false;
  return (
    isFiniteNumber(input.latitude) &&
    isFiniteNumber(input.longitude) &&
    isFiniteNumber(input.altitudeAglM) &&
    input.altitudeAglM >= MIN_AIRBORNE_ALT_M &&
    isFiniteNumber(input.gimbalPitchDeg) &&
    isFiniteNumber(input.cameraYawDeg) &&
    isFiniteNumber(input.hfovDeg) &&
    input.hfovDeg > 0 &&
    input.hfovDeg < 180 &&
    isFiniteNumber(input.vfovDeg) &&
    input.vfovDeg > 0 &&
    input.vfovDeg < 180
  );
}

/**
 * Offset a lat/lon by a local east/north displacement in metres.
 *
 * Equirectangular rather than a full geodesic: footprints are hundreds of
 * metres, where the error is centimetres, and this runs per corner per frame.
 */
function offsetLatLon(
  lat: number,
  lon: number,
  eastM: number,
  northM: number
): [number, number] {
  const dLat = (northM / EARTH_RADIUS_M) / DEG;
  const dLon = (eastM / (EARTH_RADIUS_M * Math.cos(lat * DEG))) / DEG;
  return [lon + dLon, lat + dLat];
}

/**
 * Project one corner ray onto the ground.
 *
 * Camera frame: +x right, +y up, +z forward along the optical axis. Roll is
 * applied first (about the optical axis), then pitch (elevation), then yaw
 * (compass). The result is an east/north offset from the aircraft's nadir.
 */
function projectRay(
  xTan: number,
  yTan: number,
  pitchDeg: number,
  yawDeg: number,
  rollDeg: number,
  altitudeM: number,
  maxRangeM: number
): { east: number; north: number; clipped: boolean } {
  const roll = rollDeg * DEG;
  const cosRoll = Math.cos(roll);
  const sinRoll = Math.sin(roll);
  const xr = xTan * cosRoll - yTan * sinRoll;
  const yr = xTan * sinRoll + yTan * cosRoll;

  // Rotate about the camera's right axis by the gimbal pitch. Forward is +z,
  // up is +y; after this, `up` is the world-vertical component of the ray.
  const pitch = pitchDeg * DEG;
  const cosPitch = Math.cos(pitch);
  const sinPitch = Math.sin(pitch);
  const forward = cosPitch - yr * sinPitch;
  const up = sinPitch + yr * cosPitch;
  const right = xr;

  const down = -up;
  let clipped = false;
  let scale: number;

  if (down <= MIN_DOWNWARD) {
    // Ray is at or above the horizon: it never meets the ground.
    clipped = true;
    scale = maxRangeM / Math.max(Math.hypot(forward, right), 1e-9);
  } else {
    scale = altitudeM / down;
    const groundDistance = scale * Math.hypot(forward, right);
    if (groundDistance > maxRangeM) {
      clipped = true;
      scale = maxRangeM / Math.max(Math.hypot(forward, right), 1e-9);
    }
  }

  // Yaw into world axes. At yaw 0 the camera looks north, so `forward` maps to
  // north and `right` maps to east.
  const yaw = yawDeg * DEG;
  const cosYaw = Math.cos(yaw);
  const sinYaw = Math.sin(yaw);
  const f = forward * scale;
  const r = right * scale;

  return {
    east: f * sinYaw + r * cosYaw,
    north: f * cosYaw - r * sinYaw,
    clipped,
  };
}

/** Shoelace area of a planar ring given in metres. */
function ringAreaM2(points: Array<{ east: number; north: number }>): number {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    sum += a.east * b.north - b.east * a.north;
  }
  return Math.abs(sum) / 2;
}

/**
 * Project the camera's field of view onto the ground.
 *
 * Accepts a partial input because callers hold optional telemetry fields;
 * returns null when there is not enough to project at all — see
 * `canProjectFootprint`. Callers should treat null as "no footprint to draw"
 * rather than an error.
 */
export function computeCameraFootprint(
  input: Partial<CameraFootprintInput> | null | undefined
): CameraFootprint | null {
  if (!canProjectFootprint(input)) return null;

  const {
    latitude,
    longitude,
    altitudeAglM,
    gimbalPitchDeg,
    cameraYawDeg,
    gimbalRollDeg = 0,
    hfovDeg,
    vfovDeg,
    maxRangeM = DEFAULT_MAX_RANGE_M,
  } = input;

  const xTan = Math.tan((hfovDeg / 2) * DEG);
  const yTan = Math.tan((vfovDeg / 2) * DEG);

  // Traversed around the image rectangle so the projected quad cannot
  // self-intersect: near-left, near-right, far-right, far-left. That order also
  // gives the counter-clockwise exterior ring GeoJSON asks for.
  const corners: Array<[number, number]> = [
    [-xTan, -yTan],
    [xTan, -yTan],
    [xTan, yTan],
    [-xTan, yTan],
  ];

  // Altitude-scaled, so a low aircraft cannot project to the horizon. maxRangeM
  // stays an absolute ceiling on top of that.
  const effectiveRangeM = maxUsefulRangeM(altitudeAglM, maxRangeM);

  let horizonClipped = false;
  const offsets = corners.map(([cx, cy]) => {
    const p = projectRay(
      cx,
      cy,
      gimbalPitchDeg,
      cameraYawDeg,
      gimbalRollDeg,
      altitudeAglM,
      effectiveRangeM
    );
    if (p.clipped) horizonClipped = true;
    return p;
  });

  const ring: Array<[number, number]> = offsets.map((o) =>
    offsetLatLon(latitude, longitude, o.east, o.north)
  );
  ring.push(ring[0]);

  const centerEast = offsets.reduce((s, o) => s + o.east, 0) / offsets.length;
  const centerNorth = offsets.reduce((s, o) => s + o.north, 0) / offsets.length;

  return {
    ring,
    horizonClipped,
    centerDistanceM: Math.hypot(centerEast, centerNorth),
    areaM2: ringAreaM2(offsets),
  };
}

/**
 * Pick the camera's compass yaw from the available telemetry.
 *
 * DJI documents gimbal `Attitude.yaw` as north-referenced, but on the Mavic Pro
 * that depends on gimbal mode: in a follow mode the reported yaw tracks the
 * aircraft, and using it directly would point the footprint the wrong way
 * whenever the aircraft is not flying north. When the mode says the gimbal is
 * following, the yaw relative to aircraft heading plus the heading is the
 * trustworthy combination.
 */
export function resolveCameraYawDeg(args: {
  gimbalYawDeg?: number | null;
  gimbalYawRelDeg?: number | null;
  gimbalMode?: string | null;
  headingDeg?: number | null;
}): number | null {
  const { gimbalYawDeg, gimbalYawRelDeg, gimbalMode, headingDeg } = args;
  const mode = (gimbalMode || '').toUpperCase();
  const follows = mode.includes('YAW_FOLLOW') || mode.includes('FPV');

  if (follows && isFiniteNumber(gimbalYawRelDeg) && isFiniteNumber(headingDeg)) {
    return normaliseDeg(headingDeg + gimbalYawRelDeg);
  }
  if (isFiniteNumber(gimbalYawDeg)) return normaliseDeg(gimbalYawDeg);
  if (isFiniteNumber(headingDeg)) return normaliseDeg(headingDeg);
  return null;
}

/** Wrap any angle into 0..360. */
export function normaliseDeg(deg: number): number {
  return ((deg % 360) + 360) % 360;
}
